"use client"

import type React from "react"

interface PrivacyPolicyProps {
  onBack?: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: July 22nd, 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <p className="text-gray-700 leading-relaxed">
                WWW Technologies LLC (referred to as "TopClip", "we", "us", or "our") is the entity responsible for your information under this Privacy Policy and operate the services accessible at topclip.ai.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information Collection</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect information about you in the following ways:
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Information You Provide:</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                This includes information submitted when creating an account, using our services, or contacting us for support, such as your name, email address, and the content you edit or upload.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Automated Information Collection:</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you access our Service, we automatically collect certain information about your device and usage of our Service, including IP addresses, browser type, internet service provider (ISP), referring/exit pages, operating system, date/time stamp, and clickstream data.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cookies and Tracking Technologies:</h3>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to track activity on our Service and hold certain information, enhancing your experience with our Service. We may also use third-party analytics tools to better understand user behavior and improve our Service. These tools may collect information sent by your browser, such as your IP address and interaction data, but this information is anonymized and not stored in a way that identifies you. You can opt out of some analytics tracking via your browser settings or third-party opt-out tools.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Use of Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The information we collect is used to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 leading-relaxed">
                <li>Provide, maintain, and improve our Service.</li>
                <li>Communicate with you, including to provide customer support and to respond to your inquiries.</li>
                <li>Understand and analyze how you use our Service for improvement.</li>
                <li>Detect, prevent, and address technical issues and fraud.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Sharing of Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share information we collect about you as follows:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 leading-relaxed">
                <li><strong>Service Providers:</strong> With third-party service providers who perform services on our behalf, under strict data protection terms.</li>
                <li><strong>Legal Obligations:</strong> When required by law or to respond to legal process or lawful requests, including from law enforcement and government agencies.</li>
                <li><strong>Protection of Rights and Safety:</strong> To protect the rights, property, or safety of TopClip, our users, or others, including to prevent fraud and reduce credit risk.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We take the security of your information seriously and implement reasonable security measures to protect it. However, no security system is impenetrable, and we cannot guarantee the security of our databases, nor can we guarantee that the information you supply won't be intercepted while being transmitted to us over the Internet.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">International Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to, and maintained on, computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                Depending on where you reside, you may have certain rights regarding your personal information, including the right to access, correct, delete, or restrict its use. To exercise these rights, please contact us at the information below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                For any questions please contact us at{" "}
                <a href="mailto:hello@topclip.ai" className="text-blue-600 hover:text-blue-800 underline">
                  hello@topclip.ai
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy